import React from 'react';
import { X, FileText, Calendar, User } from 'lucide-react';
import { Button } from '../ui/button';

const InvoiceDetailsModal = ({ isOpen, onClose, invoiceDetails }) => {
  if (!isOpen || !invoiceDetails) return null;

  const { invoice, line_items } = invoiceDetails;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-4xl w-full p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <FileText className="w-8 h-8 text-blue-600" />
            <div>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Invoice Details</h2>
              <p className="text-sm text-gray-500 dark:text-gray-400">#{invoice.invoice_number}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Invoice Header Info */}
        <div className="grid grid-cols-2 gap-6 mb-6">
          <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <User className="w-4 h-4 text-gray-600 dark:text-gray-400" />
              <p className="text-sm text-gray-600 dark:text-gray-400">Parent</p>
            </div>
            <p className="font-bold text-gray-900 dark:text-white">{invoice.parent_name}</p>
          </div>
          <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <Calendar className="w-4 h-4 text-gray-600 dark:text-gray-400" />
              <p className="text-sm text-gray-600 dark:text-gray-400">Period</p>
            </div>
            <p className="font-semibold text-gray-900 dark:text-white">
              {new Date(invoice.period_start).toLocaleDateString()} - {new Date(invoice.period_end).toLocaleDateString()}
            </p>
          </div>
        </div>

        {/* Line Items Table */}
        <div className="mb-6">
          <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-3">Line Items</h3>
          <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
            <table className="w-full">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300">Task</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300">Student</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300">Tutor</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-700 dark:text-gray-300">Hours</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-700 dark:text-gray-300">Rate/Hr</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-700 dark:text-gray-300">Amount</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {line_items.map((item, index) => (
                  <tr key={index} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                    <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">{item.task_name}</td>
                    <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">{item.student_name}</td>
                    <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">{item.tutor_name}</td>
                    <td className="px-4 py-3 text-sm text-right text-gray-900 dark:text-white">{item.hours_worked.toFixed(2)}</td>
                    <td className="px-4 py-3 text-sm text-right text-gray-600 dark:text-gray-400">₹{item.rate_per_hour.toFixed(2)}</td>
                    <td className="px-4 py-3 text-sm text-right font-semibold text-gray-900 dark:text-white">₹{item.amount.toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Invoice Totals */}
        <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 space-y-2">
          <div className="flex justify-between text-sm">
            <span className="text-gray-600 dark:text-gray-400">Subtotal</span>
            <span className="font-semibold text-gray-900 dark:text-white">₹{invoice.subtotal.toLocaleString()}</span>
          </div>
          {invoice.platform_charge > 0 && (
            <div className="flex justify-between text-sm">
              <span className="text-gray-600 dark:text-gray-400">Platform Charge</span>
              <span className="font-semibold text-green-600">+₹{invoice.platform_charge.toLocaleString()}</span>
            </div>
          )}
          {invoice.adjustments !== 0 && (
            <div className="flex justify-between text-sm">
              <span className="text-gray-600 dark:text-gray-400">Adjustments</span>
              <span className={`font-semibold ${invoice.adjustments > 0 ? 'text-green-600' : 'text-red-600'}`}>
                {invoice.adjustments > 0 ? '+' : ''}₹{invoice.adjustments.toLocaleString()}
              </span>
            </div>
          )}
          {invoice.discount > 0 && (
            <div className="flex justify-between text-sm">
              <span className="text-gray-600 dark:text-gray-400">Discount</span>
              <span className="font-semibold text-red-600">-₹{invoice.discount.toLocaleString()}</span>
            </div>
          )}
          <div className="flex justify-between text-sm">
            <span className="text-gray-600 dark:text-gray-400">GST ({invoice.gst_percentage}%)</span>
            <span className="font-semibold text-gray-900 dark:text-white">₹{invoice.gst_amount.toLocaleString()}</span>
          </div>
          <div className="border-t border-gray-300 dark:border-gray-600 pt-2 mt-2">
            <div className="flex justify-between">
              <span className="text-lg font-bold text-gray-900 dark:text-white">Final Amount</span>
              <span className="text-2xl font-bold text-green-600">₹{invoice.final_amount.toLocaleString()}</span>
            </div>
          </div>
        </div>

        {invoice.description && (
          <div className="mt-4 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
            <p className="text-xs text-gray-600 dark:text-gray-400 mb-1">Description</p>
            <p className="text-sm text-gray-900 dark:text-white">{invoice.description}</p>
          </div>
        )}

        <div className="flex justify-end mt-6">
          <Button onClick={onClose} variant="outline">Close</Button>
        </div>
      </div>
    </div>
  );
};

export default InvoiceDetailsModal;
